/**
 * RoleSurfacedCandidatesPanel.jsx — RC 1.62 Phase D.
 *
 * Governed Channel-2 surface mounted inside the Surfaced
 * Candidates sub-tab of the Candidates workspace. Activated ONLY
 * when the server projects `channel_2.enabled === true` on
 * /api/employer/v1/me.
 *
 * STRICT DOCTRINE:
 *   • Anonymous list by default. Identity only renders once the
 *     candidate has accepted the employer's signal (server-side
 *     reveal projection via match.revealed_profile).
 *   • Each match shows the per-card signal status (pending /
 *     accepted / declined / deferred / none) sourced from the
 *     server's per-role signals map. No client-side derivation.
 *   • Send-signal flow is metered server-side via
 *     emp_consume_signal_atomic. The frontend posts the body;
 *     the response envelope echoes back the consumption result
 *     for remaining-quota refresh.
 *   • READ + governed-write only. No batch transmission, no
 *     templates, no automated acceptance/decline. No
 *     candidate-side outreach. No contact-details exposure
 *     beyond the reveal projection.
 *   • No browsing-style discovery language. "Surfaced
 *     Candidates" externally; institutional copy.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle, AlertTriangle, ChevronDown, Inbox, Loader2, RefreshCw,
  Send, Sparkles, UserCheck,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

import { apiFetch } from '@/lib/api';
import { listReviewerAssignments } from '@/lib/employerReviewerRoster';
import RouteToReviewersButton from './RouteToReviewersButton';

function fmtTs(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year:   'numeric', month: 'short', day: '2-digit',
    hour:   '2-digit', minute: '2-digit',
  });
}

const STATUS_TONE = {
  accepted: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
  pending:  'border-amber-500/40  bg-amber-500/10  text-amber-200',
  declined: 'border-rose-500/40   bg-rose-500/10   text-rose-200',
  deferred: 'border-blue-500/40   bg-blue-500/10   text-blue-200',
};

function SignalBadge({ signal }) {
  if (!signal) return null;
  const s = signal.status || 'pending';
  return (
    <Badge
      variant="outline"
      className={`uppercase tracking-wider text-[10px] ${STATUS_TONE[s] || 'border-ndor-edge bg-ndor-surface-alt/60 text-ndor-ink-secondary'}`}
      data-testid={`surfaced-match-signal-${s}`}
    >
      Signal {s}
    </Badge>
  );
}

/**
 * PoolDiagnosticsBlock — RC 1.62 missing-CV observability.
 *
 * Renders ONLY inside the empty-matches state. Reads the
 * `pool_summary.diagnostics` projection from
 * GET /api/employer/roles/{id}/matches and surfaces a calm,
 * operational explanation of why the surface produced zero
 * matches. No design re-skin — keeps the existing card visuals
 * and adds a single inline diagnostic block.
 *
 * Doctrine:
 *   • READ-only. No buttons, no CTAs, no candidate PII.
 *   • If the backend did not include a diagnostic (service-key
 *     missing, or older deployment), nothing is rendered.
 *   • Aggregate counts only — never per-candidate.
 */
function PoolDiagnosticsBlock({ poolSummary }) {
  if (!poolSummary || !poolSummary.diagnostics) return null;
  const d = poolSummary.diagnostics;
  const noPrimary = d.no_primary_cv;
  const emptyText = d.empty_parsed_text || 0;
  const hasUsable = d.has_usable_profile || 0;
  const domainMatch = d.domain_match || 0;
  const workingOk = d.working_model_compatible || 0;
  const locationOk = d.location_compatible || 0;
  const eligible = d.eligible_total || 0;
  const pool = d.pool_size || 0;

  // Pick the most operationally-actionable single line. Order
  // chosen so the operator sees the *first* gate that collapses
  // the pool, not just the eventual eligibility number.
  let primaryReason = null;
  if (pool === 0) {
    primaryReason = 'No discoverable candidate profiles in the pool yet.';
  } else if (noPrimary !== null && noPrimary !== undefined && noPrimary >= pool) {
    primaryReason = `None of the ${pool} discoverable candidates have uploaded a Source CV. Matching can begin once at least one candidate marks a Source CV as primary.`;
  } else if (noPrimary !== null && noPrimary !== undefined && noPrimary > 0 && eligible === 0) {
    primaryReason = `${noPrimary} of ${pool} candidates have no primary Source CV. Without parsed CV text, eligibility cannot be scored.`;
  } else if (emptyText > 0 && eligible === 0) {
    primaryReason = `${emptyText} candidate(s) have a primary CV but no parsed text yet. Re-parsing or re-upload would unblock matching.`;
  } else if (hasUsable === 0 && pool > 0) {
    primaryReason = 'No candidates in the pool have a usable profile (skills or primary domain).';
  } else if (domainMatch === 0 && pool > 0) {
    primaryReason = 'No candidates match the role\'s primary domain. Broadening the must-have domain may surface more.';
  } else if (workingOk === 0 && pool > 0) {
    primaryReason = 'No candidates are compatible with the role\'s working model.';
  } else if (locationOk === 0 && pool > 0) {
    primaryReason = 'No candidates are within the role\'s location constraint.';
  }

  return (
    <div
      className="rounded border border-amber-500/20 bg-amber-500/5 px-3 py-3 space-y-2"
      data-testid="role-surfaced-pool-diagnostic"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-amber-300 shrink-0" />
        <div className="text-xs text-ndor-ink-secondary leading-relaxed">
          <p className="font-medium text-ndor-ink">Why no matches yet?</p>
          {primaryReason && (
            <p className="mt-1" data-testid="role-surfaced-pool-diagnostic-summary">
              {primaryReason}
            </p>
          )}
        </div>
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-ndor-ink-muted pl-5">
        <div className="flex justify-between gap-2">
          <dt>Pool size</dt>
          <dd data-testid="diag-pool-size" className="text-ndor-ink">{pool}</dd>
        </div>
        {noPrimary !== null && noPrimary !== undefined && (
          <div className="flex justify-between gap-2">
            <dt>No primary CV</dt>
            <dd data-testid="diag-no-primary-cv" className="text-ndor-ink">{noPrimary}</dd>
          </div>
        )}
        <div className="flex justify-between gap-2">
          <dt>Empty parsed text</dt>
          <dd data-testid="diag-empty-parsed-text" className="text-ndor-ink">{emptyText}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>Domain match</dt>
          <dd className="text-ndor-ink">{domainMatch}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>Working model OK</dt>
          <dd className="text-ndor-ink">{workingOk}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>Eligible total</dt>
          <dd data-testid="diag-eligible-total" className="text-ndor-ink">{eligible}</dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * RevealedProfilePanel — RC 1.62 reveal-surface completion.
 *
 * Renders ONLY when the operator has already received an accepted
 * signal for this match AND the backend hydrated the PII-allow-listed
 * `revealed_profile` projection in `core.reveal.build_revealed_profile`.
 *
 * No new endpoint, no new field, no new PII. The allow-list is
 * pinned at the backend:
 *   full_name, headline, location, summary, current_job_title,
 *   current_company, years_experience, primary_domain, skills[]
 *
 * Email, phone, auth UID, CV URL, and any other contact channel are
 * NOT returned by the backend and therefore cannot be rendered here.
 */
function RevealedProfilePanel({ revealed, matchId }) {
  if (!revealed) return null;
  const skills = Array.isArray(revealed.skills) ? revealed.skills.slice(0, 12) : [];
  const empLine = [revealed.current_job_title, revealed.current_company]
    .filter(Boolean).join(' · ');
  const contextLine = [
    revealed.location,
    revealed.years_experience ? `${revealed.years_experience} yrs experience` : null,
    revealed.primary_domain,
  ].filter(Boolean).join(' · ');

  return (
    <div
      className="mt-3 rounded border border-emerald-500/20 bg-emerald-500/[0.04] px-4 py-3 space-y-2"
      data-testid={`surfaced-reveal-panel-${matchId}`}
    >
      <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-200/80">
        Revealed profile
      </div>
      {revealed.full_name && (
        <div
          className="text-sm font-semibold text-ndor-ink"
          data-testid={`surfaced-reveal-name-${matchId}`}
        >
          {revealed.full_name}
        </div>
      )}
      {revealed.headline && (
        <div className="text-xs text-ndor-ink-secondary leading-relaxed">
          {revealed.headline}
        </div>
      )}
      {empLine && (
        <div className="text-xs text-ndor-ink-secondary">
          {empLine}
        </div>
      )}
      {contextLine && (
        <div className="text-[11px] text-ndor-ink-muted">
          {contextLine}
        </div>
      )}
      {revealed.summary && (
        <p className="text-xs text-ndor-ink-secondary leading-relaxed pt-1 border-t border-emerald-500/10">
          {revealed.summary}
        </p>
      )}
      {skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {skills.map((s) => (
            <Badge
              key={s}
              variant="outline"
              className="border-ndor-edge bg-ndor-surface-alt/40 text-[10px] text-ndor-ink-secondary"
            >
              {s}
            </Badge>
          ))}
        </div>
      )}
      <p className="text-[10px] text-ndor-ink-muted italic pt-1">
        Candidate identity and profile context become visible only after acceptance.
        Contact details are never exposed by this surface.
      </p>
    </div>
  );
}

export default function RoleSurfacedCandidatesPanel({
  roleId,
  accountId: accountIdProp,
  signalsRemaining,
  signalsAllowance,
  onSignalSent,
}) {
  const [matches, setMatches]     = useState(null);   // null = loading
  const [signalsMap, setSigMap]   = useState({});     // {match_id: signal}
  const [error, setError]         = useState(null);
  const [composer, setComposer]   = useState(null);   // {match_id, body, busy}
  const [refreshTok, setRefreshT] = useState(0);
  // Per-row expansion of the revealed-profile panel. Collapsed by default
  // — the badge alone is the headline state; the panel is on-demand context.
  const [openReveal, setOpenReveal] = useState({});   // {match_id: bool}
  // RC 1.62 Channel-2 Lifecycle Completion — reviewer routing context.
  // accountId comes from the prop (fast path) or /api/employer/v1/me
  // (fallback). activeReviewerCount counts active assignments for this
  // role. Both are best-effort; failure degrades the Route button to
  // a disabled state — never blocks the panel render.
  const [accountId, setAccountId] = useState(accountIdProp || null);
  const [activeReviewerCount, setActiveReviewerCount] = useState(0);
  // RC 1.62 missing-CV observability — server-emitted pool diagnostic
  // surfaced when the matches list is empty so operators understand
  // why their published role yielded zero surfaced candidates.
  const [poolSummary, setPoolSummary] = useState(null);

  const loadReviewerContext = useCallback(async () => {
    try {
      let aid = accountIdProp || null;
      if (!aid) {
        const me  = await apiFetch('/api/employer/v1/me');
        aid = me?.organisation?.id ?? me?.account_id ?? null;
      }
      setAccountId(aid);
      if (!aid) { setActiveReviewerCount(0); return; }
      const assignments = await listReviewerAssignments(aid);
      const n = (assignments || []).filter(
        (a) => a.role_id === roleId && a.assignment_status === 'active',
      ).length;
      setActiveReviewerCount(n);
    } catch {
      // Soft feature — disable routing if context fetch fails.
      setActiveReviewerCount(0);
    }
  }, [roleId, accountIdProp]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [matchesData, sigData] = await Promise.all([
        apiFetch(`/api/employer/roles/${roleId}/matches?limit=200`).catch((e) => { throw e; }),
        apiFetch(`/api/employer/roles/${roleId}/signals`).catch(() => ({ signals_by_match: {} })),
      ]);
      setMatches(Array.isArray(matchesData?.matches) ? matchesData.matches : []);
      // Backend route returns { role_id, signals_by_match: { match_id: {...} } }.
      // Reading the wrong key here was the root cause of the per-card signal
      // pill staying invisible after a successful send (RC 1.62 audit B.4).
      setSigMap(sigData?.signals_by_match || {});
      // RC 1.62 missing-CV observability — backend includes pool_summary
      // ONLY when the matches list is empty. When matches exist it stays
      // null and the explainer block is hidden.
      setPoolSummary(matchesData?.pool_summary || null);
    } catch (err) {
      const msg = err?.data?.detail?.message || err?.data?.message || err?.message || 'Unable to load Surfaced Candidates.';
      setError(msg);
      setMatches([]);
    }
  }, [roleId]);

  useEffect(() => { load(); }, [load, refreshTok]);
  useEffect(() => { loadReviewerContext(); }, [loadReviewerContext, refreshTok]);

  const openComposer = (match, idx) => {
    const label = String((idx ?? 0) + 1).padStart(3, '0');
    setComposer({ matchId: match.id, label, body: '', busy: false });
  };

  const sendSignal = async () => {
    if (!composer || !composer.body.trim()) return;
    setComposer((c) => ({ ...c, busy: true }));
    try {
      const res = await apiFetch(
        `/api/employer/roles/${roleId}/matches/${composer.matchId}/signal`,
        {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ signal_body: composer.body }),
        },
      );
      const label = composer.label ? ` · Match ${composer.label}` : '';
      if (res?.already_sent) {
        toast.warning(`A signal for this match is already pending${label}.`);
      } else {
        toast.success(`Signal sent${label}`);
      }
      setComposer(null);
      setRefreshT((t) => t + 1);
      if (typeof onSignalSent === 'function') {
        try { onSignalSent(res); } catch { /* never block */ }
      }
    } catch (err) {
      const status = err?.status || err?.data?.status;
      const code = err?.data?.detail?.code || err?.code || 'SIGNAL_FAILED';
      const msg  = err?.data?.detail?.message || err?.data?.detail || err?.data?.message || err?.message || 'Signal failed.';
      // RC 1.62 reveal-surface completion — disambiguate the
      // 409 "Access already granted" branch from a generic failure
      // so the operator never confuses an existing accepted thread
      // with a freshly-sent signal.
      if (status === 409 || /already granted|already accepted/i.test(String(msg))) {
        toast.info('This match already has an accepted signal. No new signal was sent.');
        setComposer(null);
        setRefreshT((t) => t + 1);
        return;
      }
      toast.error(`${code}: ${typeof msg === 'string' ? msg : 'Signal failed.'}`);
      setComposer((c) => (c ? { ...c, busy: false } : c));
    }
  };

  const remaining = useMemo(() => {
    if (signalsRemaining === null || signalsRemaining === undefined) return null;
    return Number(signalsRemaining);
  }, [signalsRemaining]);

  return (
    <Card className="border-ndor-edge bg-ndor-surface" data-testid="role-surfaced-candidates-panel">
      <CardHeader className="px-6 pt-6 pb-3 flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base font-semibold text-ndor-ink flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-slate-300" />
            Surfaced Candidates
          </CardTitle>
          <p className="text-xs text-ndor-ink-muted mt-1 leading-relaxed max-w-prose">
            Anonymous matches against your role. Sending a signal expresses
            governed interest; identity is revealed only after the candidate
            accepts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {remaining !== null && (
            <Badge
              variant="outline"
              className="border-ndor-edge bg-ndor-surface-alt/60 text-[10px] uppercase tracking-[0.14em] text-ndor-ink-secondary"
              data-testid="surfaced-signals-remaining"
            >
              Signals remaining {remaining}
              {signalsAllowance !== null && signalsAllowance !== undefined ? ` / ${signalsAllowance}` : ''}
            </Badge>
          )}
          <Button
            variant="ghost" size="sm"
            onClick={() => setRefreshT((t) => t + 1)}
            aria-label="Refresh"
            data-testid="role-surfaced-refresh"
            className="h-8 w-8 p-0"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="px-6 pb-6 space-y-3">
        {error && (
          <div
            className="text-xs text-rose-400 flex items-start gap-2 bg-rose-500/5 border border-rose-500/20 rounded px-3 py-2"
            data-testid="role-surfaced-error"
          >
            <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {matches === null && (
          <div className="flex items-center gap-2 text-xs text-slate-300 py-6 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        )}

        {Array.isArray(matches) && matches.length === 0 && !error && (
          <div className="text-center py-8 space-y-3" data-testid="role-surfaced-empty">
            <Inbox className="h-6 w-6 text-slate-400 mx-auto" />
            <p className="text-sm text-ndor-ink font-medium">No matches surfaced yet.</p>
            <p className="text-xs text-slate-200 leading-relaxed max-w-md mx-auto">
              Surfaced Candidates are produced by the institutional matching
              workflow. Once your role has been published and the
              discoverable pool has been evaluated, anonymous matches will
              appear here.
            </p>
            <div className="text-left max-w-md mx-auto">
              <PoolDiagnosticsBlock poolSummary={poolSummary} />
            </div>
          </div>
        )}

        {Array.isArray(matches) && matches.length > 0 && (
          <ul className="space-y-2" data-testid="role-surfaced-list">
            {matches.map((m, idx) => {
              const signal = signalsMap[m.id] || null;
              const revealed = m.revealed_profile || null;
              const fitPct = (m.fit_score !== null && m.fit_score !== undefined)
                ? Math.round(m.fit_score * 100)
                : null;
              const isExpanded = !!openReveal[m.id];
              return (
                <li
                  key={m.id}
                  className="rounded border border-ndor-edge bg-ndor-surface-alt/30 px-4 py-3"
                  data-testid={`role-surfaced-row-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-ndor-ink tabular-nums">
                          Match {String(idx + 1).padStart(3, '0')}
                        </span>
                        {revealed ? (
                          <Badge
                            variant="outline"
                            className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200 uppercase tracking-wider text-[10px]"
                            data-testid={`surfaced-revealed-${m.id}`}
                          >
                            <UserCheck className="h-3 w-3 mr-1" />
                            {revealed.full_name || 'Identity revealed'}
                          </Badge>
                        ) : null}
                        <SignalBadge signal={signal} />
                      </div>
                      <div className="mt-1 flex items-center gap-3 flex-wrap text-[11px] text-ndor-ink-secondary">
                        {fitPct !== null && (
                          <span data-testid={`surfaced-fit-${m.id}`}>Fit {fitPct}%</span>
                        )}
                        {m.surfaced_at && (
                          <span>Surfaced {fmtTs(m.surfaced_at)}</span>
                        )}
                        {/* Audit timeline — historical clarity per RC 1.62
                            reveal-surface completion. sent_at and
                            responded_at come from the existing
                            signals_by_match payload; no new fields. */}
                        {signal?.sent_at && (
                          <span data-testid={`surfaced-sent-at-${m.id}`}>
                            Sent {fmtTs(signal.sent_at)}
                          </span>
                        )}
                        {signal?.responded_at && (
                          <span data-testid={`surfaced-responded-at-${m.id}`}>
                            {signal.status === 'accepted' ? 'Accepted' :
                             signal.status === 'declined' ? 'Declined' :
                             signal.status === 'deferred' ? 'Deferred' : 'Responded'}
                            {' '}{fmtTs(signal.responded_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1">
                      {revealed && m.candidate_id && signal?.status === 'accepted' && (
                        <RouteToReviewersButton
                          matchId={m.id}
                          candidateId={m.candidate_id}
                          accountId={accountId}
                          roleId={roleId}
                          activeReviewerCount={activeReviewerCount}
                          onRouted={() => setRefreshT((t) => t + 1)}
                        />
                      )}
                      {revealed && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setOpenReveal((s) => ({ ...s, [m.id]: !s[m.id] }))}
                          className="text-xs h-8"
                          data-testid={`surfaced-reveal-toggle-${m.id}`}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? 'Collapse profile' : 'View profile context'}
                        >
                          <ChevronDown
                            className={`h-3.5 w-3.5 mr-1.5 transition-transform duration-150 ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                          />
                          {isExpanded ? 'Hide profile' : 'View profile'}
                        </Button>
                      )}
                      {!signal && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openComposer(m, idx)}
                          disabled={(remaining !== null && remaining <= 0)}
                          className="text-xs h-8"
                          data-testid={`role-surfaced-send-${m.id}`}
                        >
                          <Send className="h-3.5 w-3.5 mr-1.5" />
                          Send signal
                        </Button>
                      )}
                    </div>
                  </div>
                  {isExpanded && revealed && (
                    <RevealedProfilePanel revealed={revealed} matchId={m.id} />
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {composer && (
          <div
            className="rounded border border-ndor-edge bg-ndor-bg/60 px-4 py-3 space-y-2"
            data-testid="role-surfaced-composer"
          >
            <div className="text-xs text-ndor-ink-secondary uppercase tracking-wider">
              Compose signal · governed expression of interest
            </div>
            <Textarea
              value={composer.body}
              onChange={(e) => setComposer((c) => ({ ...c, body: e.target.value }))}
              maxLength={800}
              rows={3}
              placeholder="A short, institutional note about why this role would suit them. The candidate sees only this text — no recruiter identity, no contact details."
              className="text-sm bg-ndor-bg text-ndor-ink placeholder:text-ndor-ink-muted border-ndor-edge focus-visible:ring-1 focus-visible:ring-ndor-glow"
              data-testid="role-surfaced-composer-input"
              disabled={composer.busy}
            />
            <div className="flex items-center justify-between text-[11px] text-ndor-ink-muted">
              <span>{composer.body.length}/800</span>
              <span>
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                Sending consumes one of your monthly signals.
              </span>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <Button
                size="sm" variant="ghost"
                onClick={() => setComposer(null)}
                disabled={composer.busy}
                data-testid="role-surfaced-composer-cancel"
              >Cancel</Button>
              <Button
                size="sm"
                onClick={sendSignal}
                disabled={composer.busy || !composer.body.trim()}
                data-testid="role-surfaced-composer-send"
              >
                {composer.busy ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Sending…</>
                ) : (
                  <><Send className="h-3.5 w-3.5 mr-1.5" />Send signal</>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
